import React from "react";
import styled from "styled-components/macro";
import Lottie from "lottie-react";
import lottieFile from "./sunnySpinnerLottieData.json";
import shouldNotAnimate from "helpers/shouldNotAnimate";

interface Props {
  /**
   * Whether the loading area includes the full page, specifically from the top
   * of the page to the bottom. Sidebars next to the loading area are okay.
   */
  fullPage?: boolean;
  "aria-label"?: string;
  className?: string;
  size?: SunnySpinnerSize;
}

const $Container = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  &.fullPage {
    height: auto;
    align-items: flex-start;
    margin-top: 20vh;
  }
`;

export enum SunnySpinnerSize {
  LARGE = "100px",
  MEDIUM = "64px",
  SMALL = "36px",
}

/**
 * A loading spinner that looks like a sun!
 *
 * The animation is powered by Lottie: https://lottiefiles.com/
 *
 * Animation adjustments may need to be done in the After Effects file and re-exported:
 * https://drive.google.com/drive/folders/1HY5UnZnPx0Jf7BPqjHMHuBI4q-0miKXa
 */
export default function SunnySpinner({
  fullPage = false,
  "aria-label": ariaLabel = "Loading",
  className = "",
  size = SunnySpinnerSize.MEDIUM,
}: Props) {
  const disableAnimations = shouldNotAnimate();

  const classNamesCombined = `${fullPage ? "fullPage" : ""} ${className}`;

  return (
    <$Container className={classNamesCombined} data-testid="sunny-spinner">
      <Lottie
        animationData={lottieFile}
        autoplay={!disableAnimations}
        loop={true}
        style={{ width: size, height: size }}
        role="presentation"
      />

      <span className="visually-hidden" aria-live="polite" role="status">
        {ariaLabel}
      </span>
    </$Container>
  );
}
